<script setup>
import Commons from '@/shared/utils/commons'
import Copy from '@/shared/utils/copy'
import GridList from '../others/GridList.vue'
import SlideDetailView from '../others/SlideDetailView.vue'
import OrderDetails from '../orders/OrderDetails.vue'
import statuses from '../orders/statuses.json'
import { ref, onMounted } from 'vue'

const cols = [
  {
    name: 'Commande',
    id: 'id',
    value(el) {
      const id = el?.id || '-'
      const truncated =
        id.length > 16
          ? `${id.substring(0, 8)}...${id.substring(id.length - 8)}`
          : id
      return `
        <div class="flex items-center gap-2">
          <span class="font-mono text-xs">${truncated}</span>
          <button onclick="window.copyToClipboard('${id}')" class="p-1 hover:bg-gray-100 rounded" title="Copier l'ID">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
        </div>
      `
    },
  },
  {
    id: 'orderDate',
    name: 'Date',
    value(el) {
      return Commons.time(el?.orderDate)
    },
  },
  {
    id: 'status',
    name: 'Statut',
    filter: {
      key: 'status',
      default: '',
      options: [
        { name: 'Tout', value: '' },
        { name: 'Nouvelle', value: 'created' },
        { name: 'Acceptée', value: 'accepted' },
        { name: 'En cuisine', value: 'preparing' },
        { name: 'Prête', value: 'cooked' },
        { name: 'En livraison', value: 'deliveryStarted' },
        { name: 'Livrée', value: 'delivered' },
        { name: 'Rejetée', value: 'rejected' },
      ],
    },
    value(el) {
      const status = statuses[el?.status] || {
        name: el?.status || 'Inconnu',
        background: '#6B7280',
        color: '#FFFFFF',
      }
      return `<div style="background-color: ${status.background}; color: ${status.color}" class="inline-block px-4 py-1 rounded-full font-semibold text-sm">${status.name}</div>`
    },
  },
  {
    name: 'Restaurant',
    value(el) {
      return el?.storeName || '-'
    },
  },
  {
    name: 'Livreur',
    value(el) {
      return el?.riderName || el?.riderId || 'Non assigné'
    },
  },
  {
    name: 'Articles',
    value(el) {
      const count = el?.products?.length || 0
      return `${count} article${count > 1 ? 's' : ''}`
    },
  },
  {
    id: 'totalPrice',
    name: 'Total',
    value(el) {
      return Commons.formatNumber(el?.totalPrice || 0, { suffix: ' FCFA' })
    },
  },
  {
    name: '',
    id: 'action',
    icon: () => '/images/icons/eye.png',
    onClick(el) {
      detailData.value = el
    },
    value() {},
  },
]

// Fonctions utilitaires pour le formatage
function getOrderStatus(status) {
  const statusObj = statuses[status] || {}
  return {
    name: statusObj.name || 'Inconnu',
    bg: statusObj.background || '#6B7280',
    color: statusObj.color || '#FFFFFF',
  }
}

function formatOrderDate(date) {
  if (!date) return '-'
  const d = new Date(date)
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function calculateDeliveryTime(order) {
  if (!order.date || !order.deliveredAt) return '-'
  const start = new Date(order.date)
  const end = new Date(order.deliveredAt)
  const diff = Math.round((end - start) / 60000) // minutes
  return `${diff} min`
}

const detailData = ref(null)
// const modal = ref(null)

onMounted(() => {
  window.copyToClipboard = text => {
    Copy.toClipboard(text, 'ID copié')
  }
})
</script>

<template>
  <div class="p-5 h-full overflow-auto">
    <GridList
      title="Commandes"
      dataKey="orders"
      hasSearch
      :cols="cols"
      :cardConfig="{
        titleField: 'id',
        titlePrefix: '#',
        titleFormatter: id => {
          if (!id) return '-'
          if (id.length > 20)
            return `${id.substring(0, 10)}...${id.substring(id.length - 6)}`
          return id
        },
        subtitleField: 'storeName',
        statusField: 'status',
        extraFields: [
          {
            key: 'orderDate',
            label: 'Date',
            formatter: value => Commons.time(value),
          },
          {
            key: 'riderName',
            label: 'Livreur',
            formatter: (value, item) => value || item?.riderId || 'Non assigné',
          },
          {
            key: 'products',
            label: 'Articles',
            formatter: value =>
              `${value?.length || 0} article${(value?.length || 0) > 1 ? 's' : ''}`,
          },
          {
            key: 'totalPrice',
            label: 'Total',
            formatter: value =>
              Commons.formatNumber(value, { suffix: ' FCFA' }),
          },
        ],
      }"
      :elClickHanler="
        el => {
          detailData = el
        }
      "
      :gridCols="{ sm: 1, md: 2, lg: 2, xl: 3 }"
    ></GridList>
  </div>
  <SlideDetailView :isVisible="!!detailData" @hide="detailData = null">
    <OrderDetails :orderData="detailData"></OrderDetails>
  </SlideDetailView>
</template>
